// src/components/homepage_content/QuickSelection.js
import React, { useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const QuickSelection = () => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState('today');
  const [selectedSport, setSelectedSport] = useState('football');
  const [selectedBetType, setSelectedBetType] = useState('match_winner');
  const [selectedBets, setSelectedBets] = useState([]);
  const [totalOdds, setTotalOdds] = useState(1.0);
  
  const betTypesRef = useRef(null);

  const dates = useMemo(() => [
    { id: 'today', label: 'Today', icon: '📅', matches: 156 },
    { id: 'tomorrow', label: 'Tomorrow', icon: '⏭️', matches: 89 },
    { id: 'weekend', label: 'Weekend', icon: '🎉', matches: 234 }
  ], []);

  const sports = useMemo(() => [
    { id: 'football', name: 'Football', icon: '⚽', matches: 498, color: 'from-blue-500 to-cyan-400' },
    { id: 'basketball', name: 'Basketball', icon: '🏀', matches: 89, color: 'from-orange-500 to-red-500' },
    { id: 'tennis', name: 'Tennis', icon: '🎾', matches: 183, color: 'from-green-500 to-emerald-400' },
    { id: 'cricket', name: 'Cricket', icon: '🏏', matches: 189, color: 'from-yellow-500 to-amber-400' },
    { id: 'baseball', name: 'Baseball', icon: '⚾', matches: 76, color: 'from-red-500 to-pink-500' }
  ], []);

  const betTypes = useMemo(() => [
    { id: 'match_winner', name: 'Match Winner', short: '1X2', icon: '🏆', description: 'Pick winner or draw', width: 'min-w-[120px]' },
    { id: 'double_chance', name: 'Double Chance', short: 'DC', icon: '🔒', description: 'Two outcomes covered', width: 'min-w-[120px]' },
    { id: 'over_under', name: 'Over/Under', short: 'O/U', icon: '📊', description: 'Total score bets', width: 'min-w-[120px]' },
    { id: 'both_teams_score', name: 'Both Teams Score', short: 'BTTS', icon: '⚽⚽', description: 'Goals from both sides', width: 'min-w-[120px]' },
    { id: 'handicap', name: 'Handicap', short: 'HCP', icon: '⚖️', description: 'Score advantage bets', width: 'min-w-[120px]' },
    { id: 'correct_score', name: 'Correct Score', short: 'CS', icon: '🎯', description: 'Exact match result', width: 'min-w-[120px]' },
    { id: 'first_goal', name: 'First Goal', short: 'FG', icon: '🥅', description: 'First team to score', width: 'min-w-[120px]' },
    { id: 'half_time_full_time', name: 'HT/FT', short: 'HT/FT', icon: '⏱️', description: 'Half/Full time result', width: 'min-w-[120px]' },
    { id: 'asian_handicap', name: 'Asian HCP', short: 'A-HCP', icon: '🎌', description: 'Asian style handicap', width: 'min-w-[120px]' },
    { id: 'player_props', name: 'Player Props', short: 'PP', icon: '👤', description: 'Player performance bets', width: 'min-w-[120px]' },
    { id: 'corners', name: 'Corners', short: 'CRN', icon: '↪️', description: 'Corner kick bets', width: 'min-w-[120px]' },
    { id: 'cards', name: 'Cards', short: 'Y/R', icon: '🟨', description: 'Yellow/red card bets', width: 'min-w-[120px]' }
  ], []);

  const upcomingMatches = useMemo(() => [
    {
      id: 1,
      sport: 'football',
      league: 'Premier League',
      leagueLogo: '🏴󠁧󠁢󠁥󠁮󠁧󠁿',
      date: 'Today, 20:00',
      team1: { name: 'Manchester City', short: 'MCI', form: 'WWLWW' },
      team2: { name: 'Liverpool', short: 'LIV', form: 'WLWWW' },
      odds: {
        match_winner: [
          { value: '1.85', label: '1', popular: true },
          { value: '3.60', label: 'X' },
          { value: '4.20', label: '2' }
        ],
        double_chance: [
          { value: '1.20', label: '1X' },
          { value: '1.30', label: '12', popular: true },
          { value: '1.80', label: 'X2' }
        ],
        over_under: [
          { value: '1.45', label: 'Over 2.5', detail: '2.5 Goals' },
          { value: '2.75', label: 'Under 2.5', detail: '2.5 Goals' }
        ],
        both_teams_score: [
          { value: '1.65', label: 'Yes', detail: 'BTTS' },
          { value: '2.15', label: 'No', detail: 'BTTS' }
        ]
      },
      live: true,
      viewers: '124.5k'
    },
    {
      id: 2,
      sport: 'basketball',
      league: 'NBA',
      leagueLogo: '🏀',
      date: 'Today, 22:30',
      team1: { name: 'Boston Celtics', short: 'BOS', form: 'LWWLW' },
      team2: { name: 'Golden State Warriors', short: 'GSW', form: 'WWWLW' },
      odds: {
        match_winner: [
          { value: '2.10', label: '1', popular: true },
          { value: '1.80', label: '2' }
        ],
        double_chance: [
          { value: '1.30', label: '1X' },
          { value: '1.40', label: '12' }
        ],
        over_under: [
          { value: '1.90', label: 'Over 220.5', detail: '220.5 Points' },
          { value: '1.90', label: 'Under 220.5', detail: '220.5 Points' }
        ],
        both_teams_score: [
          { value: '1.75', label: 'Yes', detail: 'BTTS' },
          { value: '2.00', label: 'No', detail: 'BTTS' }
        ]
      },
      live: true,
      viewers: '89.2k'
    },
    {
      id: 3,
      sport: 'tennis',
      league: 'ATP Finals',
      leagueLogo: '🎾',
      date: 'Tomorrow, 15:00',
      team1: { name: 'Novak Djokovic', short: 'DJK', form: 'WWWWW' },
      team2: { name: 'Daniil Medvedev', short: 'MED', form: 'WLWWL' },
      odds: {
        match_winner: [
          { value: '1.45', label: '1', popular: true },
          { value: '2.75', label: '2' }
        ],
        double_chance: [
          { value: '1.15', label: '1X' },
          { value: '1.35', label: '12' }
        ],
        over_under: [
          { value: '1.80', label: 'Over 3.5', detail: '3.5 Sets' },
          { value: '2.00', label: 'Under 3.5', detail: '3.5 Sets' }
        ],
        both_teams_score: [
          { value: '1.90', label: 'Yes', detail: 'BTTS' },
          { value: '1.90', label: 'No', detail: 'BTTS' }
        ]
      },
      live: false,
      viewers: '67.8k'
    }
  ], []);

  const filteredMatches = useMemo(() => 
    upcomingMatches.filter(match => 
      (selectedSport === 'football' || match.sport === selectedSport) &&
      (selectedDate === 'today' || true) // Simplified for demo
    ),
    [selectedSport, selectedDate, upcomingMatches]
  );

  const handleDateSelect = useCallback((dateId) => {
    setSelectedDate(dateId);
  }, []);

  const handleSportSelect = useCallback((sportId) => {
    setSelectedSport(sportId);
  }, []);

  const handleBetTypeSelect = useCallback((betTypeId) => {
    setSelectedBetType(betTypeId);
  }, []);

  const handleBetSelect = useCallback((matchId, betType, outcomeIndex, oddValue) => {
    const betKey = `${matchId}-${betType}-${outcomeIndex}`;
    setSelectedBets(prev => {
      const isSelected = prev.some(bet => bet.key === betKey);
      if (isSelected) {
        return prev.filter(bet => bet.key !== betKey);
      } else {
        return [...prev, { key: betKey, matchId, betType, outcomeIndex, odd: parseFloat(oddValue) }];
      }
    });
  }, []);

  const handleClearBets = useCallback(() => {
    setSelectedBets([]);
    setTotalOdds(1.0);
  }, []);

  const handleAddToBetslip = useCallback(() => {
    if (selectedBets.length === 0) return;
    
    const newTotalOdds = selectedBets.reduce((acc, bet) => acc * bet.odd, 1.0);
    setTotalOdds(newTotalOdds.toFixed(2));
    
    alert(`${selectedBets.length} bet(s) added to betslip! Total Odds: ${newTotalOdds.toFixed(2)}`);
    setSelectedBets([]);
  }, [selectedBets]);

  // Calculate total odds whenever selected bets change
  useMemo(() => {
    if (selectedBets.length > 0) {
      const calculatedOdds = selectedBets.reduce((acc, bet) => acc * bet.odd, 1.0);
      setTotalOdds(calculatedOdds.toFixed(2));
    } else {
      setTotalOdds(1.0);
    }
  }, [selectedBets]);

  const getCurrentOdds = useCallback((match) => {
    return match.odds[selectedBetType] || [];
  }, [selectedBetType]);

  const getSportColor = useCallback((sportId) => {
    const sport = sports.find(s => s.id === sportId);
    return sport?.color || 'from-blue-500 to-cyan-400';
  }, [sports]);

  const scrollBetTypes = (direction) => {
    if (betTypesRef.current) {
      const scrollAmount = 200;
      betTypesRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section 
      className="relative bg-gradient-to-br from-gray-900 via-gray-900 to-blue-900/20 rounded-2xl p-6 border border-gray-800 shadow-xl overflow-hidden"
      aria-labelledby="quick-bet-heading"
    >
      {/* Animated Background Elements */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-cyan-500 to-blue-500 animate-gradient-x" />
      <div className="absolute -top-10 -right-10 w-40 h-40 bg-blue-500/5 rounded-full blur-3xl" />
      
      <div className="relative">
        {/* Header Section */}
        <div className="mb-8 text-center md:text-left">
          <div className="inline-flex items-center gap-3 mb-3">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <h2 
              id="quick-bet-heading"
              className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-white to-blue-300 bg-clip-text text-transparent"
            >
              Quick Bet
            </h2>
            <span className="px-3 py-1 bg-blue-500/20 text-blue-400 text-xs font-bold rounded-full">
              FAST
            </span>
          </div>
          <p className="text-gray-400 text-sm md:text-base">Select matches and place bets quickly on DiceAce21</p>
        </div>

        {/* Date Selection - Horizontal Scroll */}
        <div className="relative mb-8">
          <div className="flex overflow-x-auto gap-3 pb-4 scrollbar-hide">
            {dates.map(date => (
              <button
                key={date.id}
                onClick={() => handleDateSelect(date.id)}
                className={`flex items-center gap-3 px-5 py-3 rounded-xl flex-shrink-0 transition-all duration-300 border ${
                  selectedDate === date.id
                    ? 'bg-gradient-to-r from-blue-600 to-blue-800 text-white border-blue-500 shadow-lg shadow-blue-500/20 scale-105'
                    : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700 border-gray-700 hover:border-blue-500/50'
                }`}
                aria-pressed={selectedDate === date.id}
              >
                <span className="text-xl">{date.icon}</span>
                <div className="text-left">
                  <div className="font-semibold">{date.label}</div>
                  <div className="text-xs opacity-75">{date.matches} matches</div>
                </div>
              </button>
            ))}
          </div>
          <div className="absolute right-0 top-0 bottom-4 w-8 bg-gradient-to-l from-gray-900 to-transparent pointer-events-none" />
        </div>

        {/* Sport Selection - Grid with Gradient Cards */}
        <div className="mb-8">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <span className="text-blue-400">🏆</span> Select Sport
          </h3>
          <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
            {sports.map(sport => (
              <button
                key={sport.id}
                onClick={() => handleSportSelect(sport.id)}
                className={`relative group p-4 rounded-xl transition-all duration-300 border overflow-hidden ${
                  selectedSport === sport.id
                    ? `bg-gradient-to-br ${sport.color} text-white border-transparent shadow-lg scale-105`
                    : 'bg-gray-800 text-gray-400 hover:text-white border-gray-700 hover:border-blue-500/50 hover:bg-gray-700'
                }`}
                aria-pressed={selectedSport === sport.id}
              >
                <div className="text-3xl mb-2">{sport.icon}</div>
                <div className="font-medium text-sm truncate">{sport.name}</div>
                <div className="text-xs opacity-75">{sport.matches} matches</div>
                {selectedSport === sport.id && (
                  <div className="absolute -bottom-2 -right-2 w-4 h-4 rounded-full bg-white/20" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Bet Type Selection - Scrollable Row */}
        <div className="mb-8 relative">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <span className="text-blue-400">🎯</span> Bet Types
            </h3>
            <div className="flex gap-2">
              <button 
                onClick={() => scrollBetTypes('left')}
                className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
                aria-label="Scroll left"
              >
                ←
              </button>
              <button 
                onClick={() => scrollBetTypes('right')}
                className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
                aria-label="Scroll right"
              >
                →
              </button>
            </div>
          </div>
          
          <div className="relative">
            {/* Scrollable Container */}
            <div 
              ref={betTypesRef}
              className="flex overflow-x-auto gap-3 pb-4 scrollbar-hide"
              style={{ scrollBehavior: 'smooth' }}
            >
              {betTypes.map(betType => (
                <button
                  key={betType.id}
                  onClick={() => handleBetTypeSelect(betType.id)}
                  className={`flex-shrink-0 ${betType.width} p-4 rounded-xl transition-all duration-300 border text-center group ${
                    selectedBetType === betType.id
                      ? 'bg-gradient-to-br from-blue-600 to-blue-800 text-white border-blue-500 shadow-lg shadow-blue-500/30 scale-105'
                      : 'bg-gray-800 text-gray-400 hover:text-white border-gray-700 hover:border-blue-500/50 hover:bg-gray-700/80'
                  }`}
                  role="tab"
                  aria-selected={selectedBetType === betType.id}
                >
                  <div className="text-2xl mb-2 group-hover:scale-110 transition-transform">{betType.icon}</div>
                  <div className="font-bold text-lg mb-1">{betType.short}</div>
                  <div className="text-xs opacity-90 line-clamp-2">{betType.name}</div>
                  <div className="text-[10px] text-gray-500 group-hover:text-gray-400 mt-1 truncate">
                    {betType.description}
                  </div>
                </button>
              ))}
            </div>
            
            {/* Gradient Overlays */}
            <div className="absolute left-0 top-0 bottom-4 w-8 bg-gradient-to-r from-gray-900 to-transparent pointer-events-none" />
            <div className="absolute right-0 top-0 bottom-4 w-8 bg-gradient-to-l from-gray-900 to-transparent pointer-events-none" />
          </div>
        </div>

        {/* Matches Grid */}
        <div className="space-y-4">
          {filteredMatches.map(match => {
            const currentOdds = getCurrentOdds(match);
            const sportColor = getSportColor(match.sport);
            
            return (
              <div 
                key={match.id} 
                className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 rounded-xl p-5 border border-gray-700 hover:border-blue-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/10 backdrop-blur-sm"
              >
                {/* Match Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${sportColor} flex items-center justify-center text-white text-lg`}>
                      {match.leagueLogo}
                    </div>
                    <div>
                      <div className="text-white font-semibold">{match.league}</div>
                      <div className="text-gray-400 text-sm">{match.date}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    {match.live && (
                      <div className="flex items-center gap-2 px-3 py-1 bg-red-500/20 text-red-400 rounded-full">
                        <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                        <span className="text-xs font-bold">LIVE</span>
                      </div>
                    )}
                    <div className="text-gray-400 text-sm flex items-center gap-1">
                      👁️ {match.viewers}
                    </div>
                  </div>
                </div>

                {/* Teams */}
                <div className="grid grid-cols-3 gap-6 items-center mb-6">
                  {/* Team 1 */}
                  <div className="text-right">
                    <div className="font-bold text-white text-lg truncate">{match.team1.short}</div>
                    <div className="text-gray-300 text-sm truncate">{match.team1.name}</div>
                    <div className="text-gray-500 text-xs mt-1 flex justify-end gap-1">
                      {match.team1.form.split('').map((result, i) => (
                        <span key={i} className={`w-2 h-2 rounded-full ${result === 'W' ? 'bg-green-500' : result === 'L' ? 'bg-red-500' : 'bg-gray-500'}`} />
                      ))}
                    </div>
                  </div>

                  {/* VS */}
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-300 mb-2">VS</div>
                    <div className="text-xs px-3 py-1 bg-gray-800 rounded-full inline-block border border-gray-700">
                      {match.sport.toUpperCase()}
                    </div>
                  </div>

                  {/* Team 2 */}
                  <div className="text-left">
                    <div className="font-bold text-white text-lg truncate">{match.team2.short}</div>
                    <div className="text-gray-300 text-sm truncate">{match.team2.name}</div>
                    <div className="text-gray-500 text-xs mt-1 flex gap-1">
                      {match.team2.form.split('').map((result, i) => (
                        <span key={i} className={`w-2 h-2 rounded-full ${result === 'W' ? 'bg-green-500' : result === 'L' ? 'bg-red-500' : 'bg-gray-500'}`} />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Odds Grid */}
                <div className="grid grid-cols-3 gap-3">
                  {currentOdds.map((odd, index) => {
                    const betKey = `${match.id}-${selectedBetType}-${index}`;
                    const isSelected = selectedBets.some(bet => bet.key === betKey);
                    
                    return (
                      <button
                        key={`${match.id}-${index}`}
                        onClick={() => handleBetSelect(match.id, selectedBetType, index, odd.value)}
                        className={`group relative p-4 rounded-xl transition-all duration-300 text-center border overflow-hidden ${
                          isSelected
                            ? 'bg-gradient-to-br from-green-500 to-emerald-600 text-white border-green-500 shadow-lg scale-105'
                            : 'bg-gray-800 hover:bg-gray-700 text-white border-gray-700 hover:border-blue-500'
                        }`}
                        aria-label={`Select ${odd.label} at odds ${odd.value}`}
                        aria-pressed={isSelected}
                      >
                        {/* Popular tag */}
                        {odd.popular && !isSelected && (
                          <div className="absolute -top-1 -right-1">
                            <div className="px-2 py-0.5 bg-yellow-500 text-[10px] font-bold text-black rounded-bl rounded-tr">
                              HOT
                            </div>
                          </div>
                        )}
                        
                        {/* Selection indicator */}
                        {isSelected && (
                          <div className="absolute top-2 right-2">
                            <div className="w-2 h-2 rounded-full bg-white" />
                          </div>
                        )}
                        
                        <div className="font-bold text-xl mb-1">{odd.value}</div>
                        <div className="text-sm font-medium mb-1">{odd.label}</div>
                        {odd.detail && (
                          <div className="text-xs text-gray-400 group-hover:text-gray-300">{odd.detail}</div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Selected Bets Summary - Sticky Bottom */}
        {selectedBets.length > 0 && (
          <div className="sticky bottom-4 mt-8 p-5 bg-gradient-to-r from-gray-800 to-gray-900 border border-blue-500/30 rounded-xl shadow-xl animate-slide-up">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" />
                  <h3 className="text-white font-bold text-lg">
                    {selectedBets.length} Selection{selectedBets.length > 1 ? 's' : ''}
                  </h3>
                </div>
                <div className="text-gray-400 text-sm">Added to DiceAce21 betslip</div>
                
                {/* Total Odds Display */}
                <div className="mt-4 flex items-center gap-4">
                  <div className="bg-blue-500/20 px-4 py-2 rounded-lg">
                    <div className="text-xs text-blue-400">Total Odds</div>
                    <div className="text-white font-bold text-xl">{totalOdds}</div>
                  </div>
                  <div className="text-sm text-gray-400">
                    Potential Win: <span className="text-green-400 font-bold">${(10 * totalOdds).toFixed(2)}</span>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button 
                  onClick={handleClearBets}
                  className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl font-semibold transition-colors duration-300 flex items-center gap-2"
                >
                  <span>🗑️</span> Clear All
                </button>
                <button 
                  onClick={handleAddToBetslip}
                  className="px-8 py-3 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-400 hover:to-emerald-500 text-white rounded-xl font-bold transition-all duration-300 shadow-lg hover:shadow-green-500/30 flex items-center gap-2"
                >
                  <span>🎯</span> Add to Betslip
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Quick Tips */}
        <div className="mt-8 p-4 bg-gray-800/50 rounded-xl border border-gray-700">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400">
              💡
            </div>
            <h4 className="text-white font-semibold">Quick Tips</h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="text-sm text-gray-400">
              <span className="text-green-400 font-medium">✓</span> Select multiple bets for higher odds
            </div>
            <div className="text-sm text-gray-400">
              <span className="text-blue-400 font-medium">⚡</span> Live betting available for selected matches
            </div>
            <div className="text-sm text-gray-400">
              <span className="text-yellow-400 font-medium">⭐</span> Popular picks marked as "HOT"
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default QuickSelection;